// src/components/NewVehicleVariantsPricing.tsx

import { Box, Typography, Chip, Paper } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { VehicleVariant } from '../types/NewVehicleTypes';

interface NewVehicleVariantsPricingProps {
  variants: VehicleVariant[];
}

export default function NewVehicleVariantsPricing({ variants }: NewVehicleVariantsPricingProps) {
  if (!variants || variants.length === 0) return null;

  // Grab the disclaimer from the first variant that has one, or use a default
  const disclaimer = 
    variants.find(v => v.priceGroup?.priceDisclaimer)?.priceGroup?.priceDisclaimer ||
    "All prices are inclusive of COE (if applicable). Price and specifications may vary.";

  return (
    <Box mb={4}>
      <Typography variant="h6" fontWeight={800} mb={2}>Variants & Pricing</Typography>
      
      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
        
        {/* Header Row (Desktop Only) */}
        <Box display={{ xs: 'none', md: 'flex' }} bgcolor="#f5f5f5" px={2.5} py={1.5}>
          <Box flex={2}><Typography variant="body2" fontWeight={700}>Variant</Typography></Box>
          <Box flex={1.2}><Typography variant="body2" fontWeight={700}>Transmission</Typography></Box>
          <Box flex={1.2}><Typography variant="body2" fontWeight={700}>Power</Typography></Box>
          <Box flex={1.2}><Typography variant="body2" fontWeight={700}>Fuel Economy</Typography></Box>
          <Box flex={1}><Typography variant="body2" fontWeight={700}>Price</Typography></Box>
        </Box>

        {/* Data Rows */}
        {variants.map((v, idx) => (
          <Box 
            key={v.vehicleVariantId} 
            display="flex" 
            flexDirection={{ xs: 'column', md: 'row' }} 
            px={2.5} 
            py={2} 
            borderBottom={idx < variants.length - 1 ? '1px solid #e0e0e0' : 'none'}
            sx={{ '&:hover': { bgcolor: '#fafafa' } }}
          >
            {/* Variant Name & Badge */}
            <Box flex={2} display="flex" alignItems="center" gap={1.5} mb={{ xs: 1.5, md: 0 }}>
              <Typography variant="body2" fontWeight={800}>{v.variantName}</Typography>
              {v.variantBadge && (
                <Chip 
                  label={v.variantBadge} 
                  size="small" 
                  sx={{ 
                    bgcolor: '#FFF8E1', 
                    color: '#F57F17', 
                    fontWeight: 800, 
                    fontSize: 10, 
                    height: 22, 
                    borderRadius: 1 
                  }} 
                />
              )}
            </Box>

            {/* Transmission */}
            <Box flex={1.2} display="flex" alignItems="center" mb={{ xs: 0.5, md: 0 }}>
              <Typography variant="body2" >
                <Box component="span" display={{ xs: 'inline-block', md: 'none' }} width={120} fontWeight={600}>Transmission:</Box>
                {v.transmissionType || '-'}
              </Typography>
            </Box>

            {/* Power */}
            <Box flex={1.2} display="flex" alignItems="center" mb={{ xs: 0.5, md: 0 }}>
              <Typography variant="body2" >
                <Box component="span" display={{ xs: 'inline-block', md: 'none' }} width={120} fontWeight={600}>Power:</Box>
                {v.specGroup?.maximumPowerOutput ? `${v.specGroup.maximumPowerOutput} kW` : '-'}
              </Typography>
            </Box>

            {/* Fuel Economy */}
            <Box flex={1.2} display="flex" alignItems="center" mb={{ xs: 1.5, md: 0 }}>
              <Typography variant="body2" >
                <Box component="span" display={{ xs: 'inline-block', md: 'none' }} width={120} fontWeight={600}>Fuel Economy:</Box>
                {v.specGroup?.fuelConsumption ? `${v.specGroup.fuelConsumption} km/L` : '-'}
              </Typography>
            </Box>

            {/* Price */}
            <Box flex={1} display="flex" alignItems="center">
              <Box component="span" display={{ xs: 'inline-block', md: 'none' }} width={120} fontWeight={700} color="text.primary">Price:</Box>
              <Typography variant="body1" fontWeight={800} color="#FFB800">
                {v.priceGroup?.currentPrice != null ? `$${v.priceGroup.currentPrice.toLocaleString()}` : 'POA'}
              </Typography>
            </Box>
          </Box>
        ))}

        {/* Footer Disclaimer */}
        <Box px={2.5} py={1.5} borderTop="1px solid #e0e0e0" display="flex" gap={1} alignItems="flex-start">
          <InfoOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary', mt: 0.2 }} />
          <Typography variant="caption" color="text.secondary">{disclaimer}</Typography>
        </Box>
      </Paper>
    </Box>
  );
}